// Portfolio expansion candidates — markets that have been scored by
// `rankforge research` but DON'T have a site_row pointing to them yet.
//
// Operator workflow:
//   1. Run a research scan offline:
//        pnpm rankforge research --niche plumbing --states TX,FL,NC
//      Persists scored candidates into the markets table with
//      status='identified'.
//   2. Open this page → see the top unclaimed markets ranked by
//      opportunity_score (the same blended formula used in
//      packages/shared/src/scoring.ts: 25% search volume + 30%
//      competition + 25% commercial value + 10% market size + 10%
//      ranking feasibility).
//   3. Pick a market → register the domain → run bulk-build-new-sites.
//
// The page itself is a sortable table (uses the same shared table
// infrastructure shipped today). Filter by niche/state for the
// niches you actually compete in.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { marketsApi, type MarketGap } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

export function MarketGapsPage() {
  const [niche, setNiche] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [minScore, setMinScore] = useState<number>(0);

  const q = useQuery({
    queryKey: ["market-gaps", niche, state, minScore],
    queryFn: () =>
      marketsApi.gaps({
        niche: niche || undefined,
        state: state || undefined,
        minScore: minScore || undefined,
        limit: 100,
      }),
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-ink-900">Market gaps</h1>
        <p className="text-sm text-ink-600 mt-1">
          Top scored (city, niche) candidates that don't have a site in the portfolio yet.
        </p>
      </div>

      {q.isLoading && <p className="text-sm text-ink-500">Loading…</p>}

      {q.data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <Tile label="Scanned" value={q.data.totals.scanned} />
            <Tile label="Claimed" value={q.data.totals.claimed} tone="muted" />
            <Tile label="Unclaimed" value={q.data.totals.unclaimed} tone="accent" />
          </div>

          <div className="bg-white rounded-xl border border-ink-100 p-5">
            <div className="flex flex-wrap gap-3 items-end mb-4">
              <FilterInput
                label="Niche"
                value={niche}
                onChange={setNiche}
                placeholder="plumbing, roofing, …"
              />
              <FilterInput
                label="State"
                value={state}
                onChange={setState}
                placeholder="TX, FL, NC"
                maxLength={2}
              />
              <FilterInput
                label="Min score"
                value={minScore ? String(minScore) : ""}
                onChange={(v) => setMinScore(parseInt(v, 10) || 0)}
                placeholder="0-100"
                inputMode="numeric"
              />
              {(niche || state || minScore) && (
                <button
                  className="text-xs text-ink-500 hover:text-ink-900 underline"
                  onClick={() => { setNiche(""); setState(""); setMinScore(0); }}
                >
                  clear filters
                </button>
              )}
            </div>

            {q.data.gaps.length === 0 ? (
              <EmptyState totals={q.data.totals} hasFilters={!!(niche || state || minScore)} />
            ) : (
              <GapsTable rows={q.data.gaps} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: number; tone?: "muted" | "accent" }) {
  const cls = tone === "accent"
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : tone === "muted"
      ? "bg-ink-50 border-ink-200 text-ink-700"
      : "bg-white border-ink-200 text-ink-900";
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <div className="text-3xl font-display font-bold">{value.toLocaleString()}</div>
      <div className="text-xs uppercase tracking-wider opacity-70 mt-1">{label}</div>
    </div>
  );
}

function FilterInput({
  label, value, onChange, placeholder, maxLength, inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  inputMode?: "numeric";
}) {
  return (
    <label className="text-xs text-ink-600">
      <span className="block uppercase tracking-wider mb-1">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        className="border border-ink-200 rounded-md px-2 py-1.5 text-sm w-32 sm:w-40 focus:outline-none focus:ring-2 focus:ring-accent/40"
      />
    </label>
  );
}

function EmptyState({
  totals, hasFilters,
}: {
  totals: { scanned: number; claimed: number; unclaimed: number };
  hasFilters: boolean;
}) {
  if (hasFilters && totals.unclaimed > 0) {
    return (
      <div className="text-center py-12 text-sm text-ink-500 border border-dashed border-ink-200 rounded-lg">
        No gaps match these filters. <span className="text-ink-700">{totals.unclaimed}</span> unclaimed markets total — try clearing filters.
      </div>
    );
  }
  if (totals.unclaimed === 0 && totals.scanned > 0) {
    return (
      <div className="text-center py-12 text-sm text-ink-500 border border-dashed border-ink-200 rounded-lg">
        All <span className="text-ink-700">{totals.scanned}</span> scanned markets already have a site in the portfolio.
        <p className="mt-2 text-xs">
          To expand: run <code className="bg-ink-100 px-1 rounded">rankforge research --niche &lt;niche&gt; --states &lt;states&gt;</code> to scan new candidate cities.
        </p>
      </div>
    );
  }
  return (
    <div className="text-center py-12 text-sm text-ink-500 border border-dashed border-ink-200 rounded-lg">
      No markets scanned yet.
      <p className="mt-2 text-xs">
        Run <code className="bg-ink-100 px-1 rounded">rankforge research --niche &lt;niche&gt; --states &lt;states&gt;</code> to populate this view.
      </p>
    </div>
  );
}

function GapsTable({ rows }: { rows: MarketGap[] }) {
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable(
    rows,
    {
      score: (r) => r.opportunityScore,
      city: (r) => r.city,
      niche: (r) => r.niche,
      state: (r) => r.state,
      volume: (r) => r.searchVolume,
      cpc: (r) => r.cpc,
      kd: (r) => r.keywordDifficulty,
      competition: (r) => r.competitionDensity,
      scanned: (r) => (typeof r.scannedAt === "string" ? r.scannedAt : ""),
    },
    { key: "score", dir: "desc" },
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[800px]">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider border-b border-ink-100">
            <SortHeader label="Score" sortKey="score" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="Blended 0-100 (25% volume + 30% competition + 25% CPC + 10% market size + 10% feasibility)" />
            <SortHeader label="City" sortKey="city" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-left" />
            <SortHeader label="State" sortKey="state" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-left" />
            <SortHeader label="Niche" sortKey="niche" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-left" />
            <SortHeader label="Volume" sortKey="volume" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="Monthly search volume — primary keyword" />
            <SortHeader label="CPC" sortKey="cpc" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="Cost per click — proxy for commercial intent" />
            <SortHeader label="KD" sortKey="kd" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="Keyword difficulty 0-100. Lower = easier to rank." />
            <SortHeader label="Comp" sortKey="competition" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" title="Competition density 0-1. Lower = sparser SERP." />
            <SortHeader label="Scanned" sortKey="scanned" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="pb-2 text-right" />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => (
            <tr key={r.id} className="border-b border-ink-50 hover:bg-ink-50/40">
              <td className="py-2 text-right tabular-nums">
                <span className={`font-semibold ${
                  (r.opportunityScore ?? 0) >= 70 ? "text-green-700"
                  : (r.opportunityScore ?? 0) >= 50 ? "text-amber-700"
                  : "text-ink-500"
                }`}>
                  {r.opportunityScore ?? "—"}
                </span>
              </td>
              <td className="py-2 text-ink-900 font-medium">{r.city}</td>
              <td className="py-2 text-ink-600">{r.state}</td>
              <td className="py-2 text-ink-700">{r.niche}</td>
              <td className="py-2 text-right tabular-nums text-ink-700">
                {r.searchVolume == null ? <span className="text-ink-400">—</span> : r.searchVolume.toLocaleString()}
              </td>
              <td className="py-2 text-right tabular-nums text-ink-700">
                {r.cpc == null ? <span className="text-ink-400">—</span> : `$${r.cpc.toFixed(2)}`}
              </td>
              <td className="py-2 text-right tabular-nums text-ink-700">
                {r.keywordDifficulty == null ? <span className="text-ink-400">—</span> : r.keywordDifficulty}
              </td>
              <td className="py-2 text-right tabular-nums text-ink-700">
                {r.competitionDensity == null ? <span className="text-ink-400">—</span> : r.competitionDensity.toFixed(2)}
              </td>
              <td className="py-2 text-right text-xs text-ink-500">
                {new Date(r.scannedAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
